import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Webhook as WebhookIcon } from "lucide-react";
import { toast } from "sonner";
import { formatDeliveryStatusLabel, formatWebhookEventLabel } from "@/lib/labels";

const EVENTS = [
  "boleto_emitido",
  "boleto_registrado",
  "boleto_pago",
  "boleto_cancelado",
  "boleto_baixado",
  "boleto_vencido",
  "boleto_protestado",
  "pix_recebido",
];

export default function Webhooks() {
  const [configs, setConfigs] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ url: "", events: ["boleto_pago"], secret: "", active: true });

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/webhooks/config"), api.get("/webhooks/events")]);
    setConfigs(c.data); setEvents(e.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const toggle = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/webhooks/config", form);
      toast.success("Webhook configurado");
      setOpen(false);
      setForm({ url: "", events: ["boleto_pago"], secret: "", active: true });
      load();
    } catch { toast.error("Erro ao salvar webhook"); }
  };

  const del = (id) => {
  toast("Excluir este webhook?", {
    description: "O MV deixará de receber notificações neste endpoint.",
    duration: Infinity,
    action: {
      label: "Excluir",
      onClick: async () => {
        try {
          await api.delete(`/webhooks/config/${id}`);
          toast.success("Webhook excluído com sucesso");
          load();
        } catch {
          toast.error("Erro ao excluir o webhook");
        }
      },
    },
    cancel: { label: "Cancelar" },
  });
};

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Integração MV</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Webhooks</h1>
          <p className="text-sm text-zinc-500 mt-1">Notifique seu MV automaticamente quando eventos bancários ocorrerem.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="webhook-new-button">
              <Plus className="w-4 h-4 mr-2" /> Novo webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-sm w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Novo webhook</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider font-semibold">URL do MV</Label>
                <Input required type="url" placeholder="https://mv.empresa.com/webhook" value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  className="rounded-sm h-10 font-mono text-xs" data-testid="webhook-url" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider font-semibold">Secret (opcional)</Label>
                <Input type="password" value={form.secret} onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))}
                  className="rounded-sm h-10 font-mono text-xs" data-testid="webhook-secret" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-wider font-semibold">Eventos</Label>
                <div className="grid grid-cols-2 gap-2">
                  {EVENTS.map((ev) => (
                    <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.events.includes(ev)} onCheckedChange={() => toggle(ev)} data-testid={`webhook-ev-${ev}`} />
                      <span className="text-xs" title={ev}>{formatWebhookEventLabel(ev)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="rounded-sm" data-testid="webhook-submit">Salvar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-200 text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          Endpoints configurados
        </div>
        <div className="w-full overflow-x-auto no-scrollbar">
          <table className="tbl w-full whitespace-nowrap" data-testid="webhooks-config-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">URL</th>
                <th className="px-4 py-2 text-left">Eventos</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="w-16 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {configs.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-sm text-zinc-500">
                    Nenhum webhook configurado.
                  </td>
                </tr>
              )}
              {configs.map((c) => (
                <tr key={c.id} data-testid={`webhook-row-${c.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{c.url}</td>
                  <td className="px-4 py-2 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {c.events.map((e) => (
                        <span key={e} title={e} className="text-[10px] bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded-sm">
                          {formatWebhookEventLabel(e)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {c.active ? (
                      <span className="text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">Ativo</span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-sm">Inativo</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => del(c.id)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm" data-testid={`webhook-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="rounded-sm border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-200 flex flex-wrap gap-2 items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            Últimos eventos disparados
          </div>
          <span className="text-[10px] font-mono text-zinc-400">atualiza a cada 8s</span>
        </div>
        <div className="w-full overflow-x-auto no-scrollbar">
          <table className="tbl w-full whitespace-nowrap" data-testid="webhooks-events-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Quando</th>
                <th className="px-4 py-2 text-left">Evento</th>
                <th className="px-4 py-2 text-left">Boleto</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Tentativas</th>
                <th className="px-4 py-2 text-left">Erro</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-sm text-zinc-500">
                    Nenhum evento ainda.
                  </td>
                </tr>
              )}
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 font-mono text-xs">{formatDateTime(e.created_at)}</td>
                  <td className="px-4 py-2 text-xs" title={e.evento}>{formatWebhookEventLabel(e.evento)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.boleto_id?.slice(0, 8) || "-"}</td>
                  <td className="px-4 py-2">
                    {e.status === "delivered" && <span className="text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">{formatDeliveryStatusLabel(e.status)}</span>}
                    {e.status === "failed" && <span className="text-[10px] font-mono uppercase text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-sm">{formatDeliveryStatusLabel(e.status)}</span>}
                    {e.status === "pending" && <span className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm">{formatDeliveryStatusLabel(e.status)}</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs tabular">{e.attempts}</td>
                  <td className="px-4 py-2 text-xs text-red-600">{e.last_error || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
